import { getUuid } from '@sibe/utilities/common';
import type { HsRegularGeometry } from '../../graphic-engine-new/geometry/regular-geometry.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import { HsQuaternion } from '../../math-new/objects/quaternion.class';
import { HsVector3 } from '../../math-new/objects/vector3.class';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import { HsDefaultMaterial } from '../../graphic-render-new/materials/default-material.class';
import { HsGraphicLines } from '../../graphic-render-new/objects/graphic-lines.class';
import type { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import type { HsDefaultShader } from '../../graphic-render-new/shaders/classes/default-shader.class';
import { HsShaderData } from '../../graphic-render-new/shaders/classes/shader-data.class';
import { HsShaderObjectType } from '../../graphic-render-new/shaders/enums/shader-object-type.enum';
import { HsColor } from '../../math-new/objects/color.class';
import { hsMakeGeometry } from '../geometry-utils/make-geometry.function';
import type { HsRegularGeometryData } from '../geometry-utils/regular-geometry-data.class';
import { hsGenerateLineSegmentGeometryData } from '../primitives/generate-line-segment-geometry-data.function';
import { HsPrimitives } from '../primitives/primitives.class';

export class HsRotationalAxes {
  private readonly primitives: HsPrimitives;

  public constructor(
    private readonly graphics: HsGraphics,
    private queue: HsGraphicQueue,
    private readonly colors: [HsColor, HsColor, HsColor] = [
      new HsColor(1, 0, 1),
      new HsColor(0, 1, 0),
      new HsColor(0, 0, 1),
    ]
  ) {
    this.primitives = new HsPrimitives(this.graphics);
    this.primitives.setQueue(this.queue);
    const material: HsDefaultMaterial<HsDefaultShader> = this.initStandardMaterial();
    const linesMaterial: HsDefaultMaterial<HsDefaultShader> = this.initLinesMaterial();
    this.addLines(linesMaterial);
    this.addCubes(material);
  }

  public setQueue(queue: HsGraphicQueue): void {
    this.queue = queue;
  }

  private addLines(linesMaterial: HsDefaultMaterial<HsDefaultShader>): void {
    const lineData: HsRegularGeometryData = hsGenerateLineSegmentGeometryData();
    const lineGeometry: HsRegularGeometry | null = hsMakeGeometry(this.graphics, lineData);
    if (lineGeometry === null) {
      return;
    }
    const materialX: HsDefaultMaterial<HsDefaultShader> = linesMaterial.makeCopy(this.graphics.gl);
    const materialY: HsDefaultMaterial<HsDefaultShader> = linesMaterial.makeCopy(this.graphics.gl);
    const materialZ: HsDefaultMaterial<HsDefaultShader> = linesMaterial.makeCopy(this.graphics.gl);
    materialX.color = this.colors[0];
    materialY.color = this.colors[1];
    materialZ.color = this.colors[2];
    const lineX: HsGraphicLines = new HsGraphicLines(getUuid(), 'AxisLineX', lineGeometry, materialX);
    const lineY: HsGraphicLines = new HsGraphicLines(getUuid(), 'AxisLineY', lineGeometry, materialY);
    const lineZ: HsGraphicLines = new HsGraphicLines(getUuid(), 'AxisLineZ', lineGeometry, materialZ);
    const angleY: HsQuaternion = this.makeRotation([0, 1, 0], -Math.PI / 2);
    const angleZ: HsQuaternion = this.makeRotation([0, 0, 1], Math.PI / 2);
    const matrixY: HsMatrix4 = HsMatrix4.compose([0, 0, 0], angleY.values, [1, 1, 1]);
    const matrixZ: HsMatrix4 = HsMatrix4.compose([0, 0, 0], angleZ.values, [1, 1, 1]);
    lineY.worldMatrix.multiply(matrixY);
    lineZ.worldMatrix.multiply(matrixZ);
    lineX.updateBoundingSphere();
    lineY.updateBoundingSphere();
    lineZ.updateBoundingSphere();
    this.queue.addObject(lineX);
    this.queue.addObject(lineY);
    this.queue.addObject(lineZ);
  }

  private addCubes(material: HsDefaultMaterial<HsDefaultShader>): void {
    const positions: HsVector3[] = [new HsVector3(1, 0, 0), new HsVector3(0, 0, 1), new HsVector3(0, 1, 0)];
    for (let i: number = 0; i < positions.length; i++) {
      const cubeMaterial: HsDefaultMaterial<HsDefaultShader> = material.makeCopy(this.graphics.gl);
      cubeMaterial.color = this.colors[i];
      this.primitives.setMaterial(cubeMaterial);
      this.primitives.makeCube(positions[i].values, undefined, [0.1, 0.1, 0.1]);
    }
  }

  private initLinesMaterial(): HsDefaultMaterial<HsDefaultShader> {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth();
    const shader: HsDefaultShader = shaderData.makeShader();
    return new HsDefaultMaterial(this.graphics.gl, shader);
  }

  private initStandardMaterial(): HsDefaultMaterial<HsDefaultShader> {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth().addNormals().addDiffuseLighting();
    const shader: HsDefaultShader = shaderData.makeShader();
    return new HsDefaultMaterial(this.graphics.gl, shader);
  }

  private makeRotation(axis: HsHypersceneTypes.Array3, angle: number): HsQuaternion {
    return HsQuaternion.makeFromAxisAndAngle(axis, angle);
  }
}
